import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, User, Menu, X } from "lucide-react";
import { PeptigenLogo } from "./Logo";
import { useCart } from "../lib/cart";
import { useCustomer } from "../lib/customerAuth";
import HeaderSearch from "./HeaderSearch";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/currently-available", label: "Currently Available" },
  { to: "/quality", label: "Quality" },
  { to: "/same-day-dispatch", label: "Same-Day Dispatch" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count } = useCart();
  const { customer } = useCustomer();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl"
      style={{
        backgroundColor: "rgba(11, 18, 32, 0.85)",
        borderBottom: "1px solid rgba(0, 194, 214, 0.18)",
      }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] sm:h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <PeptigenLogo size={28} />
        </Link>
        <nav className="hidden xl:flex items-center gap-6" data-testid="primary-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isActive ? "text-[#00C2D6]" : "text-[#9CA8B8] hover:text-[#F7FAFC]"
                }`
              }
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <HeaderSearch />
          <Link
            to={customer ? "/account" : "/account/login"}
            className="hidden sm:inline-flex items-center gap-2 px-3 h-10 border border-[#5EEAD4]/40 hover:border-[#5EEAD4] text-[#5EEAD4] hover:text-[#F7FAFC] hover:bg-[#5EEAD4]/10 transition-colors rounded-sm pg-mono text-[10px] uppercase tracking-[0.18em]"
            data-testid="lab-access-link"
            title="Sign in to your customer account"
          >
            <User size={14} />
            <span className="hidden md:inline">
              {customer ? customer.name?.split(" ")[0] || "Account" : "Lab Access"}
            </span>
          </Link>
          <Link
            to={customer ? "/account" : "/account/login"}
            className="sm:hidden inline-flex items-center justify-center w-10 h-10 border border-[#5EEAD4]/40 text-[#5EEAD4] rounded-sm"
            aria-label="Account"
            data-testid="lab-access-mobile"
          >
            <User size={16} />
          </Link>
          <Link
            to="/cart"
            className="relative inline-flex items-center justify-center w-10 h-10 border border-[#00C2D6]/30 hover:border-[#00C2D6] transition-colors rounded-sm"
            data-testid="cart-link"
          >
            <ShoppingCart size={18} className="text-[#F7FAFC]" />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ background: "#A6FF00", color: "#0B1220" }}
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 border border-[#00C2D6]/30 text-[#F7FAFC] rounded-sm"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="xl:hidden fixed inset-0 top-[68px] sm:top-[72px] z-40"
          style={{ background: "rgba(6, 12, 22, 0.92)", backdropFilter: "blur(8px)" }}
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-drawer"
        >
          <nav
            className="bg-[#0B1220] border-t border-[#00C2D6]/20 px-4 py-6 space-y-1 max-h-[calc(100vh-72px)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 text-base font-medium rounded-sm transition-colors ${
                    isActive
                      ? "bg-[#00C2D6]/10 text-[#00C2D6] border border-[#00C2D6]/30"
                      : "text-[#9CA8B8] hover:bg-[#111A2B] border border-transparent"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-4 mt-4 border-t border-[#00C2D6]/20 space-y-2">
              <Link
                to={customer ? "/account" : "/account/login"}
                className="pg-btn-ghost w-full justify-center"
                data-testid="mobile-account-link"
              >
                <User size={14} />
                {customer ? customer.name?.split(" ")[0] || "My Account" : "Customer Lab Access"}
              </Link>
              <a
                href="mailto:info@peptigenlabs.co.uk"
                className="block text-center pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8] py-2"
              >
                info@peptigenlabs.co.uk
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
